import { User } from "features/User/models";
import { OnboardingJourneyNetworkRelation } from "models/v2/onboardingJourneyNetworkRelation";
import { titleize } from "helpers/string";
import { Email } from "./models";
import { CustomEmailRecipient, EmailRecipient, RoleEmailRecipient, UserEmailRecipient } from "./models/EmailRecipient";
import { EmailTemplate } from "../EmailTemplate/models";

export function emailPrimaryRecipientName(email: Email) {
  return email?.to?.[0]?.fullName || "";
}

export function appendEmailAbbreviation(value: string, emailType: string) {
  // Appends (w) or (p) to the email
  const abbreviation = emailType?.slice(0, 1);
  if (!abbreviation) return value;
  return `${value} (${abbreviation})`;
}

export function recipientToDisplayValue(recipient: EmailRecipient, userData: User | undefined, relationData: OnboardingJourneyNetworkRelation | undefined) {
  let value = "Loading";
  if (!recipient) {
    return value;
  }

  // Check if it's a custom email recipient
  const customRecipient = recipient as CustomEmailRecipient;
  if (customRecipient?.customEmail) {
    return customRecipient.customEmail;
  }

  // Check if it's a role-based recipient - use fetched relationData
  const roleRecipient = recipient as RoleEmailRecipient;
  if (roleRecipient?.onboardingJourneyNetworkRelationId && relationData) {
    value = appendEmailAbbreviation(titleize(relationData.name), roleRecipient.emailType);
    return value;
  }

  // Check if it's a user recipient - use fetched userData
  const userRecipient = recipient as UserEmailRecipient;
  if (userRecipient?.userId && userData) {
    value = appendEmailAbbreviation(userData.fullName, userRecipient.emailType);
    return value;
  }

  // Fallback if data is still loading
  if (recipient?.role) {
    const emailTypeTag = `(${recipient.emailType?.slice(0, 1) ?? "tbd"})`;
    value = `${titleize(recipient.role)} ${emailTypeTag}`;
  }

  return value;
}

/**
 * Returns the recipient name without email type abbreviation (for avatars)
 */
export function recipientToDisplayName(recipient: EmailRecipient, userData: User | undefined, relationData: OnboardingJourneyNetworkRelation | undefined) {
  if (!recipient) {
    return "Loading";
  }

  // Check if it's a custom email recipient
  const customRecipient = recipient as CustomEmailRecipient;
  if (customRecipient?.customEmail) {
    return customRecipient.customEmail;
  }

  // Check if it's a role-based recipient - use fetched relationData
  const roleRecipient = recipient as RoleEmailRecipient;
  if (roleRecipient?.onboardingJourneyNetworkRelationId && relationData) {
    return titleize(relationData.name);
  }

  // Check if it's a user recipient - use fetched userData
  const userRecipient = recipient as UserEmailRecipient;
  if (userRecipient?.userId && userData) {
    return userData.fullName;
  }

  // Fallback if data is still loading
  if (recipient?.role) {
    return titleize(recipient.role);
  }

  return "Loading";
}

export function cleanupEmailRecipients(formData: Partial<EmailTemplate>, attribute: "to" | "cc" | "bcc") {
  if (formData?.[attribute]?.length) {

    formData[attribute] = formData[attribute].map((recipient: any) => {
      const customRecipient = recipient as CustomEmailRecipient;
      const roleRecipient = recipient as RoleEmailRecipient;
      const userRecipient = recipient as UserEmailRecipient;

      // For existing recipients with nested objects, strip them out and keep only ID fields
      // For custom emails, only include id and customEmail
      if (customRecipient?.customEmail) {
        const cleaned: any = { customEmail: customRecipient.customEmail };
        if (customRecipient.id) {
          cleaned.id = customRecipient.id;
        }
        return cleaned;
      }

      // For role-based recipients with nested relation object, convert to ID field
      if (roleRecipient?.onboardingJourneyNetworkRelation) {
        const cleaned: any = {
          onboardingJourneyNetworkRelationId: roleRecipient.onboardingJourneyNetworkRelation.id,
          emailType: roleRecipient.emailType,
        };
        if (roleRecipient.id) {
          cleaned.id = roleRecipient.id;
        }
        return cleaned;
      }

      // For user recipients with nested user object, convert to user ID field
      if (userRecipient?.user) {
        const cleaned: any = {
          userId: userRecipient.user.id,
          emailType: userRecipient.emailType,
        };
        if (userRecipient.id) {
          cleaned.id = userRecipient.id;
        }
        return cleaned;
      }

      // For new recipients (without nested objects), return as-is
      return recipient;
    });
  }
}

export function isCustomEmailRecipient(recipient: EmailRecipient): recipient is CustomEmailRecipient {
  return "customEmail" in recipient;
}

export function isRoleEmailRecipient(recipient: EmailRecipient): recipient is RoleEmailRecipient {
  return "onboardingJourneyNetworkRelation" in recipient;
}

export function isUserEmailRecipient(recipient: EmailRecipient): recipient is UserEmailRecipient {
  return "user" in recipient;
}
